import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, animate } from "framer-motion";
import { Phone, MessageSquare, MapPin, X, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";

// Biểu tượng Zalo vẽ tay cao cấp bằng SVG
const ZaloIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2C6.48 2 2 6.03 2 11c0 2.2 1.02 4.19 2.66 5.61L3.5 20.5c-.15.3.15.6.45.45l4.35-2.18c1.16.42 2.41.63 3.7.63 5.52 0 10-4.03 10-9S17.52 2 12 2zm1.65 11.55h-3.9v-1.35l2.48-3.3H10.3V7.55h3.75v1.35l-2.48 3.3h2.08v1.35z" />
  </svg>
);

interface BranchContact {
  id: number;
  name: string;
  phone: string;
  zalo: string;
  map: string;
}

const BRANCHES: BranchContact[] = [
  {
    id: 1,
    name: "Chi nhánh Cần Thơ",
    phone: "0961393393",
    zalo: "https://zalo.me/0961393393",
    map: "https://maps.app.goo.gl/CTLocationPlaceholder",
  },
  {
    id: 2,
    name: "Chi nhánh TP. HCM",
    phone: "0961393394",
    zalo: "https://zalo.me/0961393394",
    map: "https://maps.app.goo.gl/HCMLocationPlaceholder",
  },
];

const MESSENGER_LINK = "https://m.me/nhacam.homestay";

interface SubButtonProps {
  icon: React.ReactNode;
  label: string;
  x: number;
  y: number;
  colorClass: string;
  onClick: () => void;
  index: number;
}

const SubButton: React.FC<SubButtonProps> = ({
  icon,
  label,
  x,
  y,
  colorClass,
  onClick,
  index,
}) => {
  return (
    <motion.button
      type="button"
      initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
      animate={{
        x,
        y,
        scale: 1,
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 20,
          delay: index * 0.05,
        },
      }}
      exit={{
        x: 0,
        y: 0,
        scale: 0,
        opacity: 0,
        transition: {
          duration: 0.2,
          delay: (3 - index) * 0.05,
        },
      }}
      whileHover={{
        scale: 1.15,
        y: y - 5, // Dịch chuyển lên thêm một chút khi hover để tăng hiệu ứng 3D
        boxShadow: "0 10px 25px -5px rgba(248, 113, 113, 0.4)",
      }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`absolute bottom-0 right-0 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white shadow-lg backdrop-blur-md transition-shadow duration-300 ${colorClass}`}
      aria-label={label}
    >
      {icon}
      {/* Tooltip nhỏ hiển thị khi hover */}
      <span className="absolute -top-10 right-0 hidden rounded bg-slate-900/90 px-2 py-1 text-xs text-white shadow backdrop-blur-sm group-hover:block whitespace-nowrap lg:block lg:opacity-0 lg:group-hover:opacity-100 lg:transition-opacity lg:duration-200">
        {label}
      </span>
    </motion.button>
  );
};

export const FloatingContactButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeBranchMenu, setActiveBranchMenu] = useState<"phone" | "zalo" | "map" | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isOnLeftHalf, setIsOnLeftHalf] = useState(false);
  const [isOnTopHalf, setIsOnTopHalf] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  useEffect(() => {
    const updateConstraints = () => {
      const isMobile = window.innerWidth < 1024;
      const minX = -window.innerWidth + 96;
      const maxX = 0;
      const minY = -window.innerHeight + 140;
      const maxY = isMobile ? -56 : 0;

      setDragConstraints({
        left: minX,
        right: maxX,
        top: minY,
        bottom: maxY,
      });

      // Recalculate snap positioning on resize to prevent button overflow
      const currentX = x.get();
      const midX = -window.innerWidth / 2 + 52;
      let snapX = maxX;
      let onLeft = false;

      if (currentX < midX) {
        snapX = minX;
        onLeft = true;
      }
      x.set(snapX);
      setIsOnLeftHalf(onLeft);

      const currentY = y.get();
      const clampedY = Math.max(minY, Math.min(maxY, currentY));
      y.set(clampedY);
      setIsOnTopHalf(clampedY < -window.innerHeight / 2 + 50);
    };

    // Restore previous saved coordinate positioning on mount
    const savedPosition = localStorage.getItem("floating_button_pos");
    if (savedPosition) {
      try {
        const { x: savedX, y: savedY } = JSON.parse(savedPosition);
        x.set(savedX);
        y.set(savedY);
      } catch (e) {
        console.error("Error parsing saved position:", e);
      }
    }

    updateConstraints();
    window.addEventListener("resize", updateConstraints);
    return () => window.removeEventListener("resize", updateConstraints);
  }, [x, y]);

  const handleDragEnd = (_event: any, _info: any) => {
    const isMobile = window.innerWidth < 1024;
    const currentX = x.get();
    const currentY = y.get();

    const midX = -window.innerWidth / 2 + 52;
    const minX = -window.innerWidth + 96;
    const maxX = 0;

    let snapX = maxX;
    let onLeft = false;
    if (currentX < midX) {
      snapX = minX;
      onLeft = true;
    }

    const minY = -window.innerHeight + 140;
    const maxY = isMobile ? -56 : 0;
    const snapY = Math.max(minY, Math.min(maxY, currentY));

    // Animate smoothly to snapped positions
    animate(x, snapX, { type: "spring", stiffness: 300, damping: 25 });
    animate(y, snapY, { type: "spring", stiffness: 300, damping: 25 });

    setIsOnLeftHalf(onLeft);
    setIsOnTopHalf(snapY < -window.innerHeight / 2 + 50);

    // Persist position
    localStorage.setItem("floating_button_pos", JSON.stringify({ x: snapX, y: snapY }));
  };

  // Đóng tất cả menu khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveBranchMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubButtonClick = (type: "phone" | "zalo" | "map") => {
    setActiveBranchMenu(activeBranchMenu === type ? null : type);
  };

  const handleBranchSelect = (branch: BranchContact, type: "phone" | "zalo" | "map") => {
    setActiveBranchMenu(null);
    setIsOpen(false);

    if (type === "phone") {
      window.open(`tel:${branch.phone}`, "_self");
    } else if (type === "zalo") {
      window.open(branch.zalo, "_blank", "noopener,noreferrer");
    } else if (type === "map") {
      window.open(branch.map, "_blank", "noopener,noreferrer");
    }
  };

  // Vị trí mở rộng dạng vòng cung góc dưới bên phải hoặc thay đổi linh hoạt theo toạ độ kéo thả
  const xMultiplier = isOnLeftHalf ? 1 : -1;
  const yMultiplier = isOnTopHalf ? 1 : -1;

  const subButtons = [
    {
      id: "zalo",
      label: "Zalo Chat",
      icon: <ZaloIcon className="h-6 w-6" />,
      x: 96 * xMultiplier,
      y: 0,
      colorClass: "bg-blue-600 hover:bg-blue-500",
      onClick: () => handleSubButtonClick("zalo"),
    },
    {
      id: "messenger",
      label: "Messenger",
      icon: <MessageSquare className="h-5 w-5" />,
      x: 83 * xMultiplier,
      y: 48 * yMultiplier,
      colorClass: "bg-gradient-to-tr from-cyan-500 via-purple-500 to-pink-500 hover:opacity-90",
      onClick: () => {
        window.open(MESSENGER_LINK, "_blank", "noopener,noreferrer");
        setIsOpen(false);
      },
    },
    {
      id: "phone",
      label: "Gọi Hotline",
      icon: <Phone className="h-5 w-5" />,
      x: 48 * xMultiplier,
      y: 83 * yMultiplier,
      colorClass: "bg-emerald-600 hover:bg-emerald-500",
      onClick: () => handleSubButtonClick("phone"),
    },
    {
      id: "map",
      label: "Chỉ đường",
      icon: <MapPin className="h-5 w-5" />,
      x: 0,
      y: 96 * yMultiplier,
      colorClass: "bg-amber-600 hover:bg-amber-500",
      onClick: () => handleSubButtonClick("map"),
    },
  ];

  return (
    <motion.div
      ref={containerRef}
      drag
      dragConstraints={dragConstraints}
      dragElastic={0.1}
      dragMomentum={false}
      onDragEnd={handleDragEnd}
      style={{ x, y }}
      className="fixed bottom-6 right-6 z-50 flex items-center justify-center touch-none select-none"
    >
      {/* Background Overlay mờ nhẹ khi mở nút */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setIsOpen(false);
              setActiveBranchMenu(null);
            }}
            className="fixed inset-0 z-30 bg-slate-900/10 backdrop-blur-[2px] transition-all duration-300 lg:hidden"
          />
        )}
      </AnimatePresence>

      <div className="relative h-14 w-14">
        {/* Render cụm nút con */}
        <AnimatePresence>
          {isOpen &&
            subButtons.map((btn, idx) => (
              <SubButton
                key={btn.id}
                icon={btn.icon}
                label={btn.label}
                x={btn.x}
                y={btn.y}
                colorClass={btn.colorClass}
                onClick={btn.onClick}
                index={idx}
              />
            ))}
        </AnimatePresence>

        {/* Nút chính */}
        <motion.button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            setActiveBranchMenu(null);
          }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          className="absolute inset-0 z-50 flex items-center justify-center rounded-full bg-nhacam-primary text-white shadow-xl shadow-nhacam-primary/20 outline-none hover:bg-nhacam-primary-hover focus:outline-none"
          aria-label="Liên hệ hỗ trợ"
        >
          {/* Vòng sóng phập phồng (Chỉ hiển thị khi nút đóng) */}
          {!isOpen && (
            <>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nhacam-primary/45 opacity-75 duration-1000" />
              <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-nhacam-primary/20" />
            </>
          )}

          {/* Biểu tượng chuyển đổi */}
          <motion.div
            animate={{ rotate: isOpen ? 135 : 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="flex items-center justify-center"
          >
            {isOpen ? <X className="h-6 w-6" /> : <HelpCircle className="h-7 w-7" />}
          </motion.div>
        </motion.button>

        {/* Hộp thoại lựa chọn Chi nhánh (Sleek Branch Selector Card) */}
        <AnimatePresence>
          {isOpen && activeBranchMenu && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className={cn(
                "absolute bottom-16 z-50 w-56 rounded-xl border border-nhacam-border bg-white p-3.5 shadow-2xl backdrop-blur-md",
                isOnLeftHalf ? "left-0" : "right-0"
              )}
            >
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Chọn chi nhánh hỗ trợ
              </h4>
              <div className="flex flex-col gap-1.5">
                {BRANCHES.map((branch) => (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => handleBranchSelect(branch, activeBranchMenu)}
                    className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground hover:bg-nhacam-primary/10 hover:text-nhacam-primary transition-all"
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-nhacam-primary/10 text-nhacam-primary">
                      {activeBranchMenu === "phone" && <Phone className="h-3.5 w-3.5" />}
                      {activeBranchMenu === "zalo" && <ZaloIcon className="h-3.5 w-3.5" />}
                      {activeBranchMenu === "map" && <MapPin className="h-3.5 w-3.5" />}
                    </div>
                    {branch.name}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
